import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, Chip,
    CircularProgress, Alert, IconButton, Avatar, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Breadcrumbs, Link, Skeleton
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkIcon from '@mui/icons-material/Work';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const PlayersDetailsPage = () => {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati
    const [player, setPlayer] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [updating, setUpdating] = React.useState(false);

    // Carica i dettagli del giocatore
    const loadPlayerDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/v-player`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        playerId: parseInt(playerId),
                        $limit: 1
                    }
                }
            );

            const data = response.data;
            if (data.data && data.data.length > 0) {
                setPlayer(data.data[0]);
            } else {
                setError("Giocatore non trovato");
            }

        } catch (error) {
            console.error("Errore nel recupero dettagli giocatore:", error);
            setError("Errore nel caricamento dei dettagli del giocatore");
            notify("Errore nel caricamento dei dettagli", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Gestisce il toggle visibilità
    const handleToggleVisibility = async () => {
        try {
            setUpdating(true);
            const jwt = getJwt();

            await axios.patch(
                `${host}/tbl-player/${player.playerId}`,
                { flVisibile: !player.flVisibile },
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Giocatore ${player.flVisibile ? 'nascosto' : 'mostrato'}`, { type: 'success' });

            // Aggiorna lo stato locale
            setPlayer(prev => ({
                ...prev,
                flVisibile: !prev.flVisibile
            }));

        } catch (error) {
            console.error("Errore nell'aggiornamento:", error);
            notify("Errore nell'aggiornamento", { type: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    // Carica i dati al mount e quando cambia playerId
    React.useEffect(() => {
        if (playerId) {
            loadPlayerDetails();
        }
    }, [playerId]);

    // Calcola lo stato del giocatore
    const getPlayerStatus = () => {
        if (!player) return { label: 'N/A', color: '#9e9e9e', bgcolor: '#f5f5f5' };

        if (player.flAnnullato) {
            return { label: 'Annullato', color: '#f44336', bgcolor: '#ffebee' };
        } else if (!player.flAttivo) {
            return { label: 'Inattivo', color: '#ff9800', bgcolor: '#fff3e0' };
        } else if (!player.flVisibile) {
            return { label: 'Nascosto', color: '#9e9e9e', bgcolor: '#f5f5f5' };
        } else {
            return { label: 'Attivo', color: '#4caf50', bgcolor: '#e8f5e8' };
        }
    };

    const status = getPlayerStatus();

    // Loading skeleton
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb skeleton */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>

                    {/* Header skeleton */}
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={80} height={80} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width={200} height={32} />
                            <Skeleton variant="text" width={150} height={20} />
                        </Box>
                    </Box>

                    {/* Cards skeleton */}
                    <Grid container spacing={3}>
                        {[1, 2, 3, 4].map(i => (
                            <Grid item xs={12} md={6} key={i}>
                                <Card sx={{ p: 3 }}>
                                    <Skeleton variant="text" width={120} height={24} />
                                    <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb */}
                    <Breadcrumbs sx={{ mb: 3 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/giocatori')}
                            sx={{ textDecoration: 'none' }}
                        >
                            Giocatori
                        </Link>
                        <Typography variant="body2" color="text.primary">
                            Errore
                        </Typography>
                    </Breadcrumbs>

                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadPlayerDetails}>
                                Riprova
                            </Button>
                        }
                    >
                        {error}
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/giocatori')}
                    >
                        Torna alla lista
                    </Button>
                </motion.div>
            </Box>
        );
    }

    // Main content
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/giocatori')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Giocatori
                    </Link>
                    <Typography variant="body2" color="text.primary">
                        {player?.nomeGiocatore || `Giocatore ${playerId}`}
                    </Typography>
                </Breadcrumbs>

                {/* Header con info principale */}
                <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>

                        {/* Avatar/Foto */}
                        <Avatar
                            src={player?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(player.foto)}` : undefined}
                            alt={player?.nomeGiocatore}
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: player?.foto ? 'transparent' : 'secondary.main',
                                fontSize: '2rem'
                            }}
                        >
                            {!player?.foto && player?.nomeGiocatore ?
                                player.nomeGiocatore.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                '👤'
                            }
                        </Avatar>

                        {/* Info principale */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {player?.nomeGiocatore || 'Nome non disponibile'}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={status.label}
                                    sx={{
                                        color: status.color,
                                        bgcolor: status.bgcolor,
                                        fontWeight: 500
                                    }}
                                />
                                <Chip
                                    icon={<GroupsIcon sx={{ fontSize: 16 }} />}
                                    label={player?.nomeTeam || `Team ${player?.teamId}`}
                                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }}
                                />
                                <Chip
                                    icon={<WorkIcon sx={{ fontSize: 16 }} />}
                                    label={player?.nomeRuolo || 'Ruolo N/A'}
                                    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500 }}
                                />
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                ID Giocatore: {player?.playerId}
                            </Typography>

                            {player?.note && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontStyle: 'italic',
                                        color: 'text.secondary',
                                        bgcolor: '#f5f5f5',
                                        p: 1.5,
                                        borderRadius: 1,
                                        borderLeft: '4px solid #2196f3'
                                    }}
                                >
                                    {player.note}
                                </Typography>
                            )}
                        </Box>

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/giocatori')}
                                size="small"
                            >
                                Indietro
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/giocatori/${playerId}/edit`)}
                                size="small"
                            >
                                Modifica
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={player?.flVisibile ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                onClick={handleToggleVisibility}
                                disabled={updating}
                                size="small"
                                sx={{
                                    color: player?.flVisibile ? 'text.primary' : '#ed6c02',
                                    borderColor: player?.flVisibile ? 'grey.300' : '#ed6c02',
                                    '&:hover': {
                                        borderColor: player?.flVisibile ? 'grey.400' : '#e65100',
                                        bgcolor: player?.flVisibile ? 'grey.50' : 'rgba(237, 108, 2, 0.04)'
                                    }
                                }}
                            >
                                {updating ? <CircularProgress size={16} /> : (player?.flVisibile ? 'Nascondi' : 'Mostra')}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadPlayerDetails}
                                size="small"
                            >
                                Aggiorna
                            </Button>
                        </Box>
                    </Box>
                </Card>

                {/* Dettagli organizzati in cards */}
                <Grid container spacing={3}>

                    {/* Informazioni Giocatore */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <InfoIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Informazioni Giocatore
                                </Typography>
                            </Box>

                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                        <TableCell>{player?.nomeGiocatore || 'N/A'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>ID Giocatore</TableCell>
                                        <TableCell>{player?.playerId}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>Ordinamento</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <StarIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                {player?.ordinamento || 'Non impostato'}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Card>
                    </Grid>

                    {/* Squadra */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <GroupsIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Squadra
                                </Typography>
                            </Box>

                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar
                                                    src={player?.teamFoto}
                                                    sx={{ width: 20, height: 20 }}
                                                >
                                                    {player?.nomeTeam?.charAt(0)}
                                                </Avatar>
                                                {player?.nomeTeam || 'N/A'}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>ID Team</TableCell>
                                        <TableCell>{player?.teamId}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Card>
                    </Grid>

                    {/* Ruolo */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <WorkIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Ruolo
                                </Typography>
                            </Box>

                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                        <TableCell>{player?.nomeRuolo || 'N/A'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>ID Ruolo</TableCell>
                                        <TableCell>{player?.ruoloId}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Card>
                    </Grid>

                    {/* Stato e Flags */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <VisibilityIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Stato e Visibilità
                                </Typography>
                            </Box>

                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500, width: '40%' }}>Visibile</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={player?.flVisibile ? 'Sì' : 'No'}
                                                size="small"
                                                color={player?.flVisibile ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>Attivo</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={player?.flAttivo ? 'Sì' : 'No'}
                                                size="small"
                                                color={player?.flAttivo ? 'success' : 'warning'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>Annullato</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={player?.flAnnullato ? 'Sì' : 'No'}
                                                size="small"
                                                color={player?.flAnnullato ? 'error' : 'success'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Card>
                    </Grid>

                    {/* Timestamp */}
                    <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CalendarTodayIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Timestamp
                                </Typography>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Creato il</TableCell>
                                                <TableCell>
                                                    {player?.createdAt ?
                                                        new Date(player.createdAt).toLocaleString('it-IT') :
                                                        'N/A'
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Aggiornato il</TableCell>
                                                <TableCell>
                                                    {player?.updatedAt ?
                                                        new Date(player.updatedAt).toLocaleString('it-IT') :
                                                        'N/A'
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    {/* Note se presenti */}
                    {player?.note && (
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <PersonIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Note Giocatore
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{
                                    bgcolor: '#f5f5f5',
                                    p: 2,
                                    borderRadius: 1,
                                    fontStyle: 'italic',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    {player.note}
                                </Typography>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {/* Footer con ultimo aggiornamento */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                    </Typography>
                </Box>

            </motion.div>
        </Box>
    );
};